import { VERBS, needsCall } from "./verbs";
import { TRIGGERS } from "./triggers";

// Sistema de validação de fluxo — COESO e INCREMENTAL.
//
// Cada regra é uma função pura (ctx) => issue[]. Para adicionar uma validação
// nova (conforme a gente percebe problemas), basta acrescentar uma função em
// RULES — nada mais muda. O publish roda validateFlow(ctx) e bloqueia se houver
// erros; warnings são apenas informativos.
//
// ctx (montado pelo Builder, sem acoplar ao react-flow):
//   { nodes: [{ id, verb, data }],   // nós reais (sem o gatilho)
//     triggerType, hasCall,
//     graph: { triggerId, verbById: {id->verb}, adj: {id->[id]} } }
//
// issue: { severity: "error"|"warning", message, nodeId?, ruleId }

const mk = (severity, message, nodeId, ruleId) => ({ severity, message, nodeId, ruleId });
const titleOf = verb => (VERBS[verb] && VERBS[verb].title) || verb;

// verbos que ATENDEM a chamada (depois deles a ligação está atendida)
const ANSWERING_VERBS = new Set([
  "answer", "say", "play", "gather", "dial", "conference", "transcribe", "dub", "enqueue", "dequeue", "listen", "llm", "originate",
]);

const isEmpty = v => v == null || v === "" || (Array.isArray(v) && v.length === 0);

// ---------------------------------------------------------------------------
// REGISTRY DE REGRAS — adicione novas validações aqui.
// ---------------------------------------------------------------------------
const RULES = [
  // 1) Nós de chamada (Falar/Coletar/URA/Transferir…) exigem uma chamada viva:
  //    um gatilho de chamada ou um nó "Iniciar chamada" no fluxo.
  ctx => {
    if (ctx.hasCall) return [];
    return ctx.nodes
      .filter(n => needsCall(n.verb))
      .map(n =>
        mk(
          "error",
          `"${titleOf(n.verb)}" só funciona durante uma chamada — use um gatilho de chamada ou um nó "Iniciar chamada" antes.`,
          n.id,
          "call-context"
        )
      );
  },

  // 2) "Transferir para IA" numa chamada de ENTRADA exige que a ligação já
  //    esteja ATENDIDA antes (o redirect do handoff não toca mídia, então não
  //    atende). Percorre o grafo rastreando o estado "atendida".
  ctx => {
    if (ctx.triggerType !== "inbound_call") return [];
    const { triggerId, verbById, adj } = ctx.graph;
    if (!triggerId) return [];
    const out = [];
    const seenBad = new Set();
    const seen = new Set();
    const stack = [[triggerId, false]];
    while (stack.length) {
      const [id, answered] = stack.pop();
      const key = `${id}:${answered ? 1 : 0}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const verb = verbById[id];
      if (verb === "transfer_ai" && !answered) {
        if (!seenBad.has(id)) {
          seenBad.add(id);
          out.push(
            mk(
              "error",
              `"${titleOf("transfer_ai")}" precisa da chamada já atendida — adicione um "Atender" ou um "Falar" antes dele.`,
              id,
              "transfer-ai-answer"
            )
          );
        }
        continue; // terminal
      }
      const next = answered || ANSWERING_VERBS.has(verb);
      (adj[id] || []).forEach(t => stack.push([t, next]));
    }
    return out;
  },

  // 3) Campos obrigatórios preenchidos (ex.: "Tocar áudio" sem áudio, "Enviar
  //    HTTP" sem URL, "Transferir para IA" sem agente). Data-driven: marca-se
  //    `required: true` no campo em verbs.js.
  ctx => {
    const out = [];
    ctx.nodes.forEach(n => {
      const def = VERBS[n.verb];
      if (!def || !def.fields) return;
      def.fields.forEach(f => {
        if (f.required && isEmpty((n.data || {})[f.name])) {
          out.push(mk("error", `Configure "${f.label}" em "${def.title}".`, n.id, "incomplete-fields"));
        }
      });
    });
    return out;
  },

  // 3b) Campos obrigatórios do GATILHO (ex.: "Ao receber chamada" sem número
  //     selecionado). Mesma ideia da regra 3, mas para a config do trigger.
  ctx => {
    const tdef = ctx.triggerType && TRIGGERS[ctx.triggerType];
    if (!tdef || !tdef.fields) return [];
    const out = [];
    const td = ctx.triggerData || {};
    tdef.fields.forEach(f => {
      if (f.required && isEmpty(td[f.name])) {
        out.push(mk("error", `Configure "${f.label}" no gatilho "${tdef.title}".`, ctx.graph && ctx.graph.triggerId, "trigger-fields"));
      }
    });
    return out;
  },

  // 3c) Webhook em "Responder no fluxo" SEM nó de resposta. O gatilho promete que
  //     a requisição espera o fluxo terminar e recebe o que o http_response montar;
  //     sem esse nó o chamador fica esperando para receber um corpo vazio — falha
  //     silenciosa, que só aparece do outro lado da integração.
  ctx => {
    if (ctx.triggerType !== "webhook") return [];
    const td = ctx.triggerData || {};
    if (String(td.responseStatus || "") !== "flow") return [];
    const hasResponse = (ctx.nodes || []).some(n => n.verb === "http_response");
    if (hasResponse) return [];
    return [
      mk(
        "error",
        'O gatilho está em "Responder no fluxo", mas não há nó "Resposta HTTP" — quem chamar o webhook recebe uma resposta vazia. Adicione o nó no fim do fluxo ou escolha um código fixo no gatilho.',
        ctx.graph && ctx.graph.triggerId,
        "webhook-flow-response"
      ),
    ];
  },

  // 4) Bloco de loop fechado: cada "Repetir para cada item" precisa do seu
  //    "Fim da repetição" no MESMO trecho de execução, na ordem certa. Valida
  //    sobre os handlers compilados (cada um é uma sequência linear — o que o
  //    motor roda), rastreando a profundidade do bloco: nunca pode ficar
  //    negativa (fim sem início) e tem de voltar a zero no fim (início sem fim).
  //    Bloqueia o publish — o "loop aberto" é um erro estrutural.
  ctx => {
    const handlers = ctx.handlers;
    if (!handlers) return [];
    const idOf = name => (ctx.stepNameToId && ctx.stepNameToId[name]) || undefined;
    const out = [];
    Object.keys(handlers).forEach(key => {
      const seq = Array.isArray(handlers[key]) ? handlers[key] : [];
      const open = []; // pilha de nós "loop" abertos neste handler
      seq.forEach(node => {
        if (!node || !node.verb) return;
        if (node.verb === "loop") open.push(node);
        else if (node.verb === "loop_end") {
          if (!open.length) {
            out.push(mk("error", 'Este "Fim da repetição" não tem um "Repetir para cada item" antes dele.', idOf(node._name), "loop-closed"));
          } else {
            open.pop();
          }
        }
      });
      // o que sobrou aberto = loops sem "Fim da repetição" neste trecho
      open.forEach(node =>
        out.push(mk("error", 'Este "Repetir para cada item" não foi fechado — adicione um "Fim da repetição" depois dos nós do bloco.', idOf(node._name), "loop-closed"))
      );
    });
    return out;
  },

  // 5) Nós ÓRFÃOS: todo nó útil tem que ser ALCANÇÁVEL a partir do gatilho. Pega
  //    o erro clássico do copiloto (ligar o gatilho num nó errado e deixar o resto
  //    solto). NÃO dá falso positivo em fluxo simples (gatilho → enviar): lá não
  //    há órfão. Nós desabilitados são ignorados (podem ficar soltos de propósito).
  ctx => {
    const { triggerId, adj } = (ctx.graph || {});
    if (!triggerId || !adj) return [];
    const reachable = new Set([triggerId]);
    const queue = [triggerId];
    while (queue.length) {
      const id = queue.shift();
      (adj[id] || []).forEach(t => {
        if (!reachable.has(t)) {
          reachable.add(t);
          queue.push(t);
        }
      });
    }
    return ctx.nodes
      .filter(n => !reachable.has(n.id) && !(n.data && n.data.disabled))
      .map(n => mk("error", `"${titleOf(n.verb)}" está desconectado do gatilho — ligue-o ao fluxo ou remova.`, n.id, "orphan-node"));
  },

  // 6) Ramo de CHAMADA que termina num nó de BACKEND (Executar código/HTTP/Dados/
  //    IA/contexto) sem nenhuma ação de voz depois: o jambonz recebe resposta
  //    vazia → a chamada CAI sem áudio. Pega o erro clássico do copiloto (monta a
  //    comparação e esquece de Falar o resultado + Desligar). Todo ramo de voz
  //    tem que TERMINAR num verbo de chamada (Falar/Desligar/Transferir/…).
  ctx => {
    if (!ctx.hasCall) return []; // automação sem chamada pode terminar em backend
    const { adj } = ctx.graph || {};
    if (!adj) return [];
    // verbos que ENCERRAM a chamada de forma aceitável mesmo não sendo "de mídia"
    const TERMINAL_OK = new Set(["hangup", "leave", "transfer_ai", "redirect", "sip:refer", "sip:decline", "loop", "loop_end"]);
    const multiOut = verb => {
      const d = VERBS[verb] || {};
      return !!(d.isBranch || d.isIvr || d.isWaMenu);
    };
    return ctx.nodes
      .filter(n => !(n.data && n.data.disabled))
      .filter(n => ((adj[n.id] || []).length === 0)) // é folha (não liga a ninguém)
      .filter(n => !needsCall(n.verb) && !TERMINAL_OK.has(n.verb) && !multiOut(n.verb))
      .map(n =>
        mk(
          "error",
          `Depois de "${titleOf(n.verb)}" a chamada encerra sem áudio e cai. Adicione um "Falar" (o resultado) e/ou "Desligar" depois — todo ramo de voz tem que terminar falando/encerrando.`,
          n.id,
          "call-deadend-backend"
        )
      );
  },

  // 7) FERRAMENTA (tool): a IA aprende os parâmetros pela lista "Parâmetros de
  //    entrada" (triggerConfig.inputParams). Então todo parâmetro USADO no fluxo
  //    tem que estar DECLARADO — senão a IA não sabe que precisa enviá-lo (foi o
  //    caso do Movidesk: id vinha vazio). Também pega o contexto errado: numa
  //    ferramenta os dados da IA chegam em steps.trigger.input.* — steps.trigger.
  //    event.* (gatilho/webhook) NÃO existe aqui e resolve sempre vazio.
  ctx => {
    if (ctx.triggerType !== "tool") return [];
    const declared = new Set((ctx.inputParams || []).map(p => p && p.name).filter(Boolean));
    const out = [];
    const seen = new Set();
    ctx.nodes.forEach(n => {
      const blob = JSON.stringify(n.data || {});
      // parâmetros usados: {{steps.trigger.input.NOME}}
      const re = /steps\.trigger\.input\.([A-Za-z0-9_]+)/g;
      let m;
      while ((m = re.exec(blob))) {
        const name = m[1];
        const key = `undecl:${n.id}:${name}`;
        if (!declared.has(name) && !seen.has(key)) {
          seen.add(key);
          out.push(mk("error", `A ferramenta usa o parâmetro "${name}", mas ele não está declarado em "Parâmetros de entrada". Declare-o (nome + descrição) para a IA saber que precisa enviá-lo.`, n.id, "tool-param-undeclared"));
        }
      }
      // contexto errado (steps.trigger.event.*) — sempre vazio numa ferramenta
      if (/steps\.trigger\.event\./.test(blob) && !seen.has(`ctx:${n.id}`)) {
        seen.add(`ctx:${n.id}`);
        out.push(mk("error", `Numa ferramenta os dados da IA chegam em "steps.trigger.input.*" — "steps.trigger.event.*" é de gatilho/webhook e fica vazio aqui. Ajuste a referência e declare o parâmetro.`, n.id, "tool-wrong-context"));
      }
    });
    return out;
  },

  // 8) FERRAMENTA sem "Retornar": o valor entregue à IA vem SÓ do nó "Retornar".
  //    Sem ele, a ferramenta até roda (e loga a saída dos nós), mas devolve `null`
  //    → a IA acha que "não encontrou". Foi o caso do buscar-ticket do Movidesk.
  ctx => {
    if (ctx.triggerType !== "tool") return [];
    if (ctx.nodes.some(n => n.verb === "return")) return [];
    return [mk("error", 'A ferramenta não tem um nó "Retornar" — sem ele a IA recebe nada (a busca "não encontra"). Adicione um "Retornar" com o resultado, ex.: {{steps.<nó>.json}}.', null, "tool-missing-return")];
  },

  // 8b) COLETOR sem "Retornar ao coletor": sem esse nó terminal o coletor não
  //     entrega dataset nenhum. Espelha a regra 8 da ferramenta. Os campos
  //     obrigatórios do nó (lista + campo sequencial) já são checados pela regra 3.
  ctx => {
    if (ctx.triggerType !== "collector") return [];
    if (ctx.nodes.some(n => n.verb === "collector_return")) return [];
    return [mk("error", 'O coletor não tem um nó "Retornar ao coletor" — adicione-o no fim do fluxo com a lista de registros e o campo sequencial.', null, "collector-missing-return")];
  },

  // 9) Nó de CÓDIGO com {{...}}: dentro do corpo do código as {{variáveis}} NÃO
  //    são substituídas (só valem em campos de config: url/body/text/…). Elas
  //    chegam LITERAIS → bug silencioso (ex.: parseInt("{{context.attempts}}")
  //    = NaN → contador sempre 0 → loop). No código, ler valores é JS puro:
  //    steps.<nó>.<campo> e inputs.session.<campo>. Foi a raiz do loop do app 17.
  ctx =>
    ctx.nodes
      .filter(n => n.verb === "code" && n.data && typeof n.data.code === "string" && n.data.code.includes("{{"))
      .map(n =>
        mk(
          "warning",
          `No "${titleOf("code")}" há {{variáveis}} — elas NÃO são substituídas dentro do código (chegam como texto; ex.: parseInt("{{…}}")=NaN → contador sempre 0 → loop). Para LER valores use JS: steps.<nó>.<campo> e inputs.session.<campo> (ex.: parseInt(inputs.session.attempts) || 0). Ignore se o {{…}} for de propósito numa string de saída.`,
          n.id,
          "code-no-template"
        )
      ),

  // GET com Body preenchido: o corpo é DESCARTADO no envio — GET não carrega
  // corpo (o próprio fetch recusa). O campo fica na tela e some em silêncio, então
  // o dado nunca chega ao destino. Avisa em vez de deixar o usuário depurar o
  // outro lado à toa.
  ctx =>
    ctx.nodes
      .filter(n => n.verb === "http_request" && !(n.data && n.data.disabled))
      .filter(n => String((n.data && n.data.method) || "POST").toUpperCase() === "GET")
      .filter(n => String((n.data && n.data.body) || "").trim())
      .map(n =>
        mk(
          "warning",
          `Este "Enviar HTTP" é GET e tem Body preenchido — GET não envia corpo, então esses dados NÃO chegam ao destino. Use POST, ou mande os valores na URL como query (?campo={{steps...}}).`,
          n.id,
          "http-get-com-body"
        )
      ),

  // Pausa abaixo de 1000ms no Coletar: o Deepgram recusa a conexão
  // ("utterance_end_ms=500, but this is beneath the configured step size") e o
  // jambonz derruba a CHAMADA INTEIRA quando o reconhecedor não conecta. Falha
  // silenciosa e cara: a ligação cai com ~1s e o fluxo ainda executa no vazio.
  ctx =>
    ctx.nodes
      .filter(n => n.verb === "gather" && !(n.data && n.data.disabled))
      // sem "fala" na coleta o recognizer nem é emitido — o valor antigo fica
      // guardado no nó mas não chega ao Deepgram, então não há o que avisar.
      .filter(n => {
        const inp = n.data && Array.isArray(n.data.input) && n.data.input.length ? n.data.input : ["digits"];
        return inp.indexOf("speech") >= 0;
      })
      .filter(n => (n.data && n.data.endMode ? n.data.endMode : "pause") === "pause")
      .filter(n => {
        const v = Number(n.data && n.data.silenceMs);
        return Number.isFinite(v) && v > 0 && v < 1000;
      })
      .map(n =>
        mk(
          "error",
          `A "Pausa que encerra a fala" está em ${n.data.silenceMs}ms — o Deepgram exige no mínimo 1000ms e recusa a conexão abaixo disso, o que derruba a chamada logo no início. Use 1000 ou mais.`,
          n.id,
          "gather-pausa-minima"
        )
      ),

  // Headers do "Enviar HTTP" que não são JSON válido: o build faz
  // jsonOr(headers, undefined) e o campo inteiro SOME — a requisição sai sem
  // nenhum cabeçalho e o usuário depura o outro lado achando que mandou.
  // Acontece o tempo todo ao colar um curl (aspas do shell viram JSON quebrado).
  ctx =>
    ctx.nodes
      .filter(n => n.verb === "http_request" && !(n.data && n.data.disabled))
      .filter(n => {
        const t = String((n.data && n.data.headers) || "").trim();
        if (!t) return false; // vazio é legítimo: sem headers extras
        try {
          const o = JSON.parse(t);
          return !o || typeof o !== "object" || Array.isArray(o);
        } catch {
          return true;
        }
      })
      .map(n =>
        mk(
          "error",
          `Os Headers deste "Enviar HTTP" não são um JSON válido — eles são DESCARTADOS e a requisição sai sem nenhum cabeçalho. Use o formato {"Nome": "valor", "Outro": "valor"}.`,
          n.id,
          "http-headers-invalidos"
        )
      ),

  // URL com espaço ou aspa: quase sempre sobra de um curl colado. O caractere vai
  // codificado no caminho (%20, %27), o destino não reconhece a rota e devolve
  // 302/404 — erro que não se parece em nada com a causa.
  ctx =>
    ctx.nodes
      .filter(n => n.verb === "http_request" && !(n.data && n.data.disabled))
      // tira os {{tokens}} antes de olhar: "{{ steps.x }}" tem espaços legítimos
      .filter(n => /['"\s]/.test(String((n.data && n.data.url) || "").trim().replace(/\{\{[^{}]*\}\}/g, "")))
      .map(n =>
        mk(
          "error",
          `A URL deste "Enviar HTTP" tem aspas ou espaços sobrando — eles viram %27/%20 no caminho e o destino responde 302/404 em vez de processar. Deixe só o endereço.`,
          n.id,
          "http-url-suja"
        )
      ),
];

// Roda todas as regras (isolando falhas de uma regra das outras).
export const validateFlow = ctx => {
  const issues = [];
  RULES.forEach(rule => {
    try {
      const r = rule(ctx) || [];
      r.forEach(i => issues.push(i));
    } catch (e) {
      /* uma regra com bug não deve travar o publish */
    }
  });
  return issues;
};

export const flowErrors = ctx => validateFlow(ctx).filter(i => i.severity === "error");
